import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { AnyAction } from "redux";
import { IStoreState } from "../initialStoreState";
import { api } from "../../api/api";
import { ICustomerContact, IPersonalInformation } from "../customer/customer.types";
import { IUserProfile } from "../UserProfileList/userProfile.types";
import { showMessage } from "../messages/messagesActions";

// --- Synchronous Actions ---

// Open/Close/Reset Panel
export const OPEN_POLICY_EMAIL_PANEL = "OPEN_POLICY_EMAIL_PANEL";
export const CLOSE_POLICY_EMAIL_PANEL = "CLOSE_POLICY_EMAIL_PANEL";
export const RESET_POLICY_EMAIL_STATE = "RESET_POLICY_EMAIL_STATE";

export const openPolicyEmailPanel = (
  customerId: number,
  producerCode: string | null,
) => action(OPEN_POLICY_EMAIL_PANEL, { customerId, producerCode });

export const closePolicyEmailPanel = () => action(CLOSE_POLICY_EMAIL_PANEL);

export const resetPolicyEmailState = () => action(RESET_POLICY_EMAIL_STATE);

// --- Async Action Constants for Thunks ---

// Recipient Loading
export const FETCH_RECIPIENT_EMAILS_PROGRESS =
  "FETCH_RECIPIENT_EMAILS_PROGRESS";
export const FETCH_RECIPIENT_EMAILS_SUCCESS = "FETCH_RECIPIENT_EMAILS_SUCCESS";
export const FETCH_RECIPIENT_EMAILS_FAILED = "FETCH_RECIPIENT_EMAILS_FAILED";

export const fetchRecipientEmailsProgress = () =>
  action(FETCH_RECIPIENT_EMAILS_PROGRESS);
export const fetchRecipientEmailsSuccess = (emails: string[]) =>
  action(FETCH_RECIPIENT_EMAILS_SUCCESS, { emails });
export const fetchRecipientEmailsFailed = () =>
  action(FETCH_RECIPIENT_EMAILS_FAILED);

// Producer Email Loading
export const FETCH_PRODUCER_EMAIL_PROGRESS = "FETCH_PRODUCER_EMAIL_PROGRESS";
export const FETCH_PRODUCER_EMAIL_SUCCESS = "FETCH_PRODUCER_EMAIL_SUCCESS";
export const FETCH_PRODUCER_EMAIL_FAILED = "FETCH_PRODUCER_EMAIL_FAILED";

export const fetchProducerEmailProgress = () =>
  action(FETCH_PRODUCER_EMAIL_PROGRESS);
export const fetchProducerEmailSuccess = (email: string | null) =>
  action(FETCH_PRODUCER_EMAIL_SUCCESS, { email });
export const fetchProducerEmailFailed = () =>
  action(FETCH_PRODUCER_EMAIL_FAILED);

// Sending Email
export const SEND_POLICY_EMAIL_PROGRESS = "SEND_POLICY_EMAIL_PROGRESS";
export const SEND_POLICY_EMAIL_SUCCESS = "SEND_POLICY_EMAIL_SUCCESS";
export const SEND_POLICY_EMAIL_FAILED = "SEND_POLICY_EMAIL_FAILED";

export const sendPolicyEmailProgress = () =>
  action(SEND_POLICY_EMAIL_PROGRESS);
export const sendPolicyEmailSuccess = () => action(SEND_POLICY_EMAIL_SUCCESS);
export const sendPolicyEmailFailed = (error: string) =>
  action(SEND_POLICY_EMAIL_FAILED, { error });

// --- Async Thunks ---

export const resolveRecipientEmailsAsync =
  (customerId: number): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchRecipientEmailsProgress());

      // Step 1: Try secondary contacts API
      let emails: string[] = [];
      let secondaryContactsFailed = false;

      try {
        const res = await api.get(
          `/customer/get-secondary-contact?customer_id=${customerId}`,
        );
        const contacts: ICustomerContact[] = res.data.data;
        // Inline extraction of non-empty emails (extractEmailsFromContacts will be created in Task 3.1)
        emails = contacts
          .map((contact) => contact.email)
          .filter(
            (email): email is string =>
              email !== null && email !== undefined && email.trim() !== "",
          );
      } catch {
        // Secondary contacts failed — silently fall back to customer details (Req 2.6)
        secondaryContactsFailed = true;
      }

      // Step 2: If no emails found from secondary contacts, fall back to customer details
      if (emails.length === 0) {
        try {
          const res = await api.get(
            `/customer/get-customer-data?customer_id=${customerId}`,
          );
          const data: IPersonalInformation[] = res.data.data;
          if (data.length > 0) {
            const primaryEmail = data[0].email;
            if (
              primaryEmail !== null &&
              primaryEmail !== undefined &&
              primaryEmail.trim() !== ""
            ) {
              emails = [primaryEmail];
            }
          }
        } catch {
          // Both APIs failed — show warning snackbar (Req 2.7)
          if (secondaryContactsFailed) {
            dispatch(
              showMessage({
                type: "warning",
                message: "Contact information could not be loaded",
                displayAs: "snackbar",
              }),
            );
          }
        }
      }

      dispatch(fetchRecipientEmailsSuccess(emails));
    } catch {
      dispatch(
        showMessage({
          type: "warning",
          message: "Contact information could not be loaded",
          displayAs: "snackbar",
        }),
      );
      dispatch(fetchRecipientEmailsFailed());
    }
  };

export const fetchProducerEmailAsync =
  (producerCode: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchProducerEmailProgress());

      const advanceFilter = JSON.stringify([
        {
          column: ["user_code"],
          operator: "EQUAL",
          value: producerCode,
        },
      ]);

      const res = await api.get(
        `/user/get-users?advanceFilter=${encodeURIComponent(advanceFilter)}`,
      );
      const users: IUserProfile[] = res.data.data;

      if (users.length > 0 && users[0].email && users[0].email.trim() !== "") {
        dispatch(fetchProducerEmailSuccess(users[0].email));
      } else {
        dispatch(fetchProducerEmailSuccess(null));
        dispatch(
          showMessage({
            type: "warning",
            message: "Producer email not found",
            displayAs: "snackbar",
          }),
        );
      }
    } catch {
      dispatch(fetchProducerEmailSuccess(null));
      dispatch(
        showMessage({
          type: "warning",
          message: "Producer email not found",
          displayAs: "snackbar",
        }),
      );
    }
  };

// --- Send Email Payload Interface ---

export interface ISendEmailPayload {
  from_email: string;
  from_name: string;
  subject: string;
  body: string;
  emails: { email: string; attachments: string[] }[];
  objectVariables?: Record<string, any>;
  // Fields used for saving email history after successful send
  module_name?: string | null;
  module_code?: number | null;
  to?: string[];
  cc?: string[];
  bcc?: string[];
  templateId?: string | null;
  bind_key?: string | null;
  bind_value?: string | number | null;
}

// --- Send Policy Email Thunk ---

export const sendPolicyEmailAsync =
  (
    payload: ISendEmailPayload,
    onComplete?: () => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(sendPolicyEmailProgress());

      await api.post("/general/send-email-to-multipleuser", { ...payload });

      // Save email history after successful send
      if (payload.module_name || payload.module_code) {
        const attachmentPaths = payload.emails
          .flatMap((e) => e.attachments)
          .filter((p) => !!p);

        try {
          // await api.post("/general/upsert-email-history", {
          //   email_history_code: null,
          //   email_conversation_id: null,
          //   module_name: payload.module_name ?? null,
          //   module_code: payload.module_code ?? null,
          //   from_email: payload.from_email ?? null,
          //   to_mail_ids: payload.to ?? payload.emails.map((e) => e.email),
          //   cc_mail_ids: payload.cc ?? [],
          //   bcc_mail_ids: payload.bcc ?? [],
          //   subject: payload.subject ?? null,
          //   body: { content: payload.body ?? null, contentType: "html" },
          //   attachments: attachmentPaths,
          //   comment: null,
          //   send_received_ts: null,
          //   status: "ACTIVE",
          // });
        } catch {
          // Email history save failure is non-blocking — email was already sent
        }
      }

      dispatch(sendPolicyEmailSuccess());
      dispatch(
        showMessage({
          type: "success",
          message: "Emails sent successfully!",
          displayAs: "snackbar",
        }),
      );

      if (onComplete) {
        onComplete();
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || "Failed to send emails";
      dispatch(sendPolicyEmailFailed(errorMessage));
      dispatch(
        showMessage({
          type: "error",
          message: errorMessage,
          displayAs: "snackbar",
        }),
      );
    }
  };
